<template>
    <div>
        <Panel
            :title="$t('Facebook Accounts')"
            :add-button="canEdit ? $t('Add Account') : ''"
            :delete-button="canEdit && selected.length > 0"
            :page="page"
            :total-rows="totalRows"
            @change="changePage"
            @delete="handleDelete"
            @input="openForm(-1)"
        >
            <table id="facebookaccounts_table" class="cic-table">
                <thead>
                    <tr>
                        <th>{{ $t('Page Name') }}</th>
                        <th>{{ $t('Page ID') }}</th>
                        <th v-if="canEdit" class="td-checkbox"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="account in accounts" @click="(evt) => handleOpen(account.lAccountID, evt)">
                        <td class="primary">
                            <el-tooltip
                                v-if="account.szUserID !== null"
                                :content="$t('This legacy account should be updated')"
                                placement="left"
                                effect="light"
                                class="legacy-account-warning-tooltip"
                            >
                                <WarningFilled id="legacy-account-warning" />
                            </el-tooltip>
                            {{ account.szAccountName }}
                        </td>
                        <td id="page_id" class="select-all">
                            {{ account.szPageID }}
                        </td>
                        <td v-if="canEdit">
                            <cic-checkbox
                                :value="selected.indexOf(account.lAccountID) !== -1"
                                @click="handleSelected(account.lAccountID)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </Panel>

        <EditFacebookAccount
            v-if="item !== null && !$store.showLoadingOverlay"
            :item="item"
            @blur="closePopup"
            @input="saveItem"
            @change="updateFieldValue"
        />

        <div v-if="$store.showLoadingOverlay" class="cic-overlay"></div>
    </div>
</template>

<script>
    import { ElTooltip } from 'element-plus';
    import { WarningFilled } from '@element-plus/icons-vue';
    import Panel from '@/components/container/Panel';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import TableUtils from '@/mixins/TableUtils';
    import { deleteItems, load, post } from '@/utils/Http';
    import { showErrorMessage, showSuccessMessage } from '@/utils/Notification';
    import EditFacebookAccount from './EditFacebookAccount';
    import './styles/facebookAccounts.scss';

    export default {
        name: 'FacebookAccounts',
        components: {
            CicCheckbox,
            EditFacebookAccount,
            Panel,
            WarningFilled,
            'el-tooltip': ElTooltip,
        },
        mixins: [TableUtils],
        props: {
            authorise: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                selected: [],
                accounts: [],
                page: 1,
                totalRows: 0,
                item: null,
            };
        },
        computed: {
            canEdit() {
                return this.$store.hasRight('EDIT_ACCESS_TYPE_16');
            },
        },
        created() {
            if (this.authorise) {
                const account = window.localStorage.getItem('facebookAccount');
                window.localStorage.removeItem('facebookAccount');
                const urlParams = new URLSearchParams(window.location.search);

                if (urlParams.get('error') || !account) {
                    showErrorMessage(
                        this.$t(
                            'Update failed. Please make sure you are logged into facebook with a user that can administer the required page.',
                        ),
                    );
                    this.loadTable();
                } else {
                    this.createAccount(account, urlParams.get('code'));
                }
            } else {
                this.loadTable();
            }
        },
        methods: {
            createAccount(data, code) {
                this.$store.hideStatusMessage();
                const saveSuccess = (response) => {
                    const account = JSON.parse(data);
                    if (response.duplicate) {
                        showSuccessMessage(this.$t('Updated duplicate account'));
                    } else if (account.lAccountID > 0) {
                        showSuccessMessage(this.$t('Update successful'));
                    } else {
                        showSuccessMessage(this.$t('Add successful'));
                    }
                    this.loadTable();
                };

                load('facebook/update', { data, code }, saveSuccess, 'Saving...');
            },

            loadTable() {
                load('facebook/get', { page: this.page }, (response) => {
                    this.totalRows = response.totalRows;
                    this.accounts = response.accounts;
                });
            },

            openForm(accountId) {
                if (accountId > 0) {
                    load('facebook/edit', { accountId: accountId }, (response) => {
                        this.item = response.data;
                    });
                } else {
                    this.item = {
                        lAccountID: null,
                        lOrganisationID: this.$store.user.orgId,
                        szAccountName: '',
                        szPageID: '',
                        bEnableFacebookPublic: false,
                        bEnableFacebookDirect: false,
                        szTimeOutMessage: null,
                        bEnableInstagramDirect: false,
                        bEnableInstagramPublic: false,
                        szUserID: null,
                    };
                }
            },

            handleDelete() {
                if (
                    confirm(
                        this.$t('By deleting this Page, any Access Points associated with this Page will be deleted'),
                    )
                ) {
                    deleteItems('facebook/delete', { accountIds: this.selected }, () => {
                        this.selected = [];
                        this.loadTable();
                    });
                }
            },

            saveItem() {
                const loading = this.$loading({
                    text: this.$t('Redirecting to Facebook...'),
                });
                const data = JSON.stringify(this.item);
                this.item = null;
                setTimeout(() => {
                    const promise = post('facebook/url', { data }, (response) => {
                        this.closePopup();
                        this.selected = [];
                        window.localStorage.setItem('facebookAccount', JSON.stringify(response.account));
                        window.location.assign(response.url);
                    });
                    promise.then((response) => {
                        if (response.status !== 'OK') {
                            loading.close();
                            this.item = JSON.parse(data);
                            showErrorMessage(this.$t(response.errorMessage));
                        }
                    });
                }, 1000);
            },
        },
    };
</script>
